import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Container, CssBaseline, Grid } from '@material-ui/core';
import appEventBus, { API_ADD_ACTIVE_AD_GROUP_HEADER } from 'config/appEventBus';
import OrderDetailsPage from 'modules/clients/pages/OrderDetailsPage';
import { ConfigurationManager } from './utils/config';
import { FeatureTogglesProvider } from './components/FeatureTogglesProvider';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import PrivateRoute from './components/PrivateRoute';
import TogglesPage from './pages/TogglesPage';
import GlobalNav from './components/GlobalNav';
// import VoiceCall from './components/VoiceCall';
import SelectActiveADGroup from './components/SelectActiveADGroup';
import DivisionListLoader from './components/DivisionListLoader';
import AuditUserOptions from './components/AuditUserOptions';
import SunsetPage from './components/SunsetPage';
import StoreMappingLoader from './components/StoreMappingLoader';

// Lazy Loading modules
const AlterationsModule = lazy(() => import('../alterations'));
const Performance = lazy(() => import('../performance'));
const Looks = lazy(() => import('../looks'));
const ClientsModule = lazy(() => import('../clients'));
const ClientelingModule = lazy(() => import('../clienteling'));
const HomeModule = lazy(() => import('../home'));
const NotificationsModule = lazy(() => import('../notifications'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const ProductsModule = lazy(() => import('../products'));
const CartModule = lazy(() => import('../cart'));
const SearchModule = lazy(() => import('../search'));

const App = ({
  config,
  setUser,
  addNotification,
  isUserLogged,
  isSuperUser,
  userId,
  userBrand,
  userADGroups,
  activeADGroup,
  classes,
  logger,
  userStore,
}) => {
  const [overrideSunsetPage, setOverrideSunsetPage] = React.useState(false);

  React.useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async (user) => {
        const userCustomGroups =
          user.attributes && user.attributes['custom:groups'] ? JSON.parse(user.attributes['custom:groups']) : [];

        setUser({ ...user.attributes, activeADGroup });

        logger.updateContext({
          associatePin: user.attributes && user.attributes['custom:employeeNumber'],
          activeADGroup: activeADGroup || userCustomGroups?.[0],
        });

        // TODO: Workaround for AWS amplify
        localStorage.removeItem('amplify-signin-with-hostedUI');
      })
      .catch((e) => {
        console.log(e?.message); /* eslint-disable-line */
      });

    // passes initial state of active AD group (actual for super user) to API services
    // More details https://neimanmarcus.atlassian.net/browse/OSP-4228
    if (activeADGroup) {
      appEventBus.emit(API_ADD_ACTIVE_AD_GROUP_HEADER, activeADGroup);
    }

    // Initialize communication with Service Workers through "message" event
    // to get notification about any errors which should showed to user
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const data = JSON.parse(event.data);
        if (data.error) {
          addNotification({
            id: `sw-error-${Date.now()}`,
            message: data.message,
            direction: 'bottomCenter',
            type: 'warning',
          });
        }
      });
    }
  }, []);

  if (!isUserLogged) {
    return null;
  }

  if (isSuperUser && !activeADGroup) {
    return <SelectActiveADGroup />;
  }

  //The following ADGroups are sunsetted & moved to Connect 2.0. Addl groups to be added in waves.
  const sunsetArray = [
    'G_OSP_BG_STORE_ASSOCIATE',
    'G_OSP_BG_INSTORE_SALES',
    'G_OSP_BG_STORE_GENERAL_MANAGER',
    'G_OSP_BG_STORE_MANAGER',
    'G_OSP_NM_STORE_ASSOCIATE',
    // 'G_OSP_NM_INSTORE_SALES',
    'G_OSP_NM_STORE_GENERAL_MANAGER',
    'G_OSP_NM_STORE_MANAGER',
    'G_OSP_NM_DIGITAL_STYLIST',
    'G_OSP_NM_DIGITAL_CLIENT_ADVISOR',
    'G_OSP_NM_STYLIST_MANAGER',
  ];

  //The following ADGroups are Audit Users
  const auditUserArray = ['G_OSP_R', 'G_OSP_R_TEST'];

  // Allowed AD Groups
  const allowedADGroups = ['G_OSP_NM_BOH', 'G_OSP_BG_BOH', 'G_OSP_NM_BOH_TEST', 'G_OSP_BG_BOH_TEST'];

  /**
   * Checks if any string in a given input array matches active Active Directory (AD) groups.
   * If in test mode, it appends "_TEST" to the input strings before checking and also
   * verifies if any of the input array matches AD groups intersect with a provided list of allowed AD groups.
   */
  const checkForADGroups = (inputArray, arrayADGroups, options = {}) => {
    const { isTest = false, allowedADGroups = [] } = options;

    return inputArray.some((inputString) => {
      const groupString = isTest ? `${inputString}_TEST` : inputString;
      const hasGroup = arrayADGroups.includes(groupString);

      if (isTest) {
        const isAllowed = arrayADGroups.some((element) => allowedADGroups.includes(element));
        return hasGroup || isAllowed;
      }

      return hasGroup;
    });
  };

  const isSunsettedUser = checkForADGroups(sunsetArray, userADGroups);
  const isSunsettedTestUser = checkForADGroups(sunsetArray, userADGroups, { isTest: true, allowedADGroups });
  const isAuditUser = checkForADGroups(auditUserArray, userADGroups);

  const specialSplashPage = (flag) => (
    <>
      <CssBaseline />
      <Container maxWidth="sm" className={classes.container}>
        <Grid container className={classes.gridContainer} wrap="nowrap">
          <Grid item className={classes.content}>
            {flag === 'AuditUserOptions' && <AuditUserOptions />}
            {flag === 'SunsetPage' && (
              <SunsetPage isTestUser={isSunsettedTestUser} overrideSunset={() => setOverrideSunsetPage(true)} />
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );

  //Displays Audit User Page for Audit User AD Groups
  if (isAuditUser) return specialSplashPage('AuditUserOptions');

  //Displays Sunset Page for AD Groups that have been moved to 2.0
  if (isSunsettedUser && !overrideSunsetPage) return specialSplashPage('SunsetPage');

  return (
    <FeatureTogglesProvider
      config={config}
      logger={logger}
      userId={userId}
      brand={userBrand}
      userADGroup={activeADGroup || userADGroups?.[0]}
      store={userStore}
    >
      <CssBaseline />
      <DivisionListLoader />
      <StoreMappingLoader />
      <Container maxWidth="sm" className={classes.container}>
        {/* <VoiceCall /> */}
        <Grid container className={classes.gridContainer} wrap="nowrap">
          <Grid item className={classes.content}>
            <Switch>
              <Route exact path="/" component={HomePage} />
              <Route path="/logout" component={HomePage} />
              {/* Lazy Loading modules */}
              <PrivateRoute path="/alterations" component={AlterationsModule} lazy />
              <PrivateRoute path="/performance" component={Performance} lazy />
              <PrivateRoute path="/looks" component={Looks} lazy />
              <PrivateRoute path="/clients" component={ClientsModule} lazy />
              <PrivateRoute path="/clienteling" component={ClientelingModule} lazy />
              <PrivateRoute path="/home" component={HomeModule} lazy />
              <PrivateRoute path="/help" component={HelpPage} lazy />
              <PrivateRoute path="/toggles" component={TogglesPage} />
              <PrivateRoute path="/search" component={SearchModule} lazy />
              <PrivateRoute path="/orders/:orderId/:channel" component={OrderDetailsPage} key="oh_details" />

              <PrivateRoute path="/notifications" component={NotificationsModule} lazy />
              <PrivateRoute path="/products" component={ProductsModule} lazy />
              <PrivateRoute path="/cart" component={CartModule} lazy />
              <Route path="*" exact component={NotFoundPage} />
            </Switch>
          </Grid>
          <Grid item>
            <GlobalNav />
          </Grid>
        </Grid>
      </Container>
    </FeatureTogglesProvider>
  );
};

App.propTypes = {
  config: PropTypes.instanceOf(ConfigurationManager).isRequired,
  setUser: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  isUserLogged: PropTypes.bool.isRequired,
  isSuperUser: PropTypes.bool.isRequired,
  userId: PropTypes.string.isRequired,
  userBrand: PropTypes.string.isRequired,
  userADGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeADGroup: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string),
  logger: PropTypes.shape({}).isRequired,
};

export default App;
